import { CSSProperties, FC, MouseEvent } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import IconChecksSvg from "../../../assets/svg-fc/IconChecksSvg";
import IconXSvg from "../../../assets/svg-fc/IconXSvg";
import { NOTIFICATION_CATEGORY_STYLES, notificationTypeSVG } from "../../../constants/notificationConstant";
import { iNotificationRowModel } from "../../../types/notificationTypes";
import { formatNotificationTime, NOTIFICATION_EVENT_LABELS } from "../../../utils/notificationUtils";
import "./notificationCard.scss";

interface iNotificationCardProps {
	notification: iNotificationRowModel;
	size?: "compact" | "large";
	className?: string;
	onOpen?: (event: MouseEvent<HTMLAnchorElement>) => void;
	onMarkRead?: () => void;
	onRemove?: () => void;
}

const stopAnd = (action: () => void) => (event: MouseEvent) => {
	event.preventDefault();
	event.stopPropagation();
	action();
};

const NotificationCard: FC<iNotificationCardProps> = ({
	notification,
	size = "compact",
	className = "",
	onOpen,
	onMarkRead,
	onRemove,
}) => {
	const { category, type, tag, eventName, eventLabel, title, body, isRead, createdAt, url } = notification;
	const style = NOTIFICATION_CATEGORY_STYLES[category];
	const isLarge = size === "large";
	const hasActions = !!(onMarkRead || onRemove);
	const timeText = formatNotificationTime(createdAt);

	return (
		<Link
			to={url}
			onClick={onOpen}
			className={`notification_card notification_card_${size} ${isRead ? "" : "unread"} ${hasActions ? "has_actions" : ""} ${className}`}
			style={
				{
					"--notification-icon": style.icon,
					"--notification-text": style.text,
					"--notification-tint": style.tint,
				} as CSSProperties
			}
		>
			<span className="notification_card_icon">{notificationTypeSVG(category, type, isLarge ? 20 : 16)}</span>
			<span className="notification_card_body">
				<span className="notification_card_tagline">
					<span className="notification_card_tag">{tag}</span>
					{eventName && (
						<>
							<span className="notification_card_separator">·</span>
							<span className="notification_card_meta">{eventName}</span>
						</>
					)}
					{eventLabel && NOTIFICATION_EVENT_LABELS[eventLabel] && (
						<>
							<span className="notification_card_separator">·</span>
							<span className="notification_card_meta notification_card_label">
								{NOTIFICATION_EVENT_LABELS[eventLabel]}
							</span>
						</>
					)}
					<span className="notification_card_trailing">
						{timeText && (
							<time
								className="notification_card_time"
								dateTime={createdAt ?? undefined}
								title={moment(createdAt).format("D MMM YYYY, h:mm A")}
							>
								{timeText}
							</time>
						)}
						{!isRead && <span className="notification_card_dot" aria-label="Unread" />}
						{hasActions && (
							<span className="notification_card_actions">
								{onMarkRead && !isRead && (
									<button
										type="button"
										aria-label="Mark as read"
										title="Mark as read"
										onClick={stopAnd(onMarkRead)}
									>
										<IconChecksSvg size={14} />
									</button>
								)}
								{onRemove && (
									<button type="button" aria-label="Remove" title="Remove" onClick={stopAnd(onRemove)}>
										<IconXSvg size={14} />
									</button>
								)}
							</span>
						)}
					</span>
				</span>
				<span className="notification_card_title">{title}</span>
				{body && <span className="notification_card_text" dangerouslySetInnerHTML={{ __html: body }} />}
			</span>
		</Link>
	);
};
export default NotificationCard;
